import type { CompileContext } from "../compile/context.ts";
import { compileRuleSetForWorld } from "../compile/resolve-map.ts";
import type { MapDocument, TerrainLayer, WorldDocument } from "../document/types.ts";
import type { CompiledStage } from "../terrain/compile-rules.ts";
import { validateTerrainFaces, type TerrainFaceIssue } from "../terrain/faces.ts";
import type { CellRect } from "../terrain/match.ts";
import type { TerrainDefinition, TerrainFace } from "../terrain/types.ts";
import { drawnFaceHeight } from "./face-height.ts";
import { createTerrainSession, setSessionFaceHeight, updateTerrainSession, type TerrainSession } from "./incremental.ts";

/**
 * Empty tiles around a terrain sample's painted patch on every side.
 */
const SAMPLE_MARGIN = 4;

/**
 * The incremental sessions of a map's terrain layers (ADR-020, ADR-021 item 4): created from the document,
 * rebuilt when a layer's index, terrain or corners change under it, and re-synced over the vertices a
 * command touched. A layer's face height (ADR-072 decision 6) re-runs its session's face pass alone. The
 * context's face declarations are checked once for each context value: one `validateTerrainFaces` rejects is treated as
 * absent, so a stale or malformed context draws the pack's height and never stops a world from opening.
 */
export class SessionManager {
  private readonly sessions = new Map<string, TerrainSession>();
  private readonly stageCache = new Map<string, CompiledStage[]>();
  private checked: { readonly context: CompileContext; readonly malformed: readonly TerrainFaceIssue[]; readonly rejected: ReadonlySet<string> } | null = null;

  /**
   * @param source Compile context (terrains, rule sets), or a function that gives the one that stands now, for a
   *   store whose library grows (ADR-130 decision 2.8). The face declarations are checked again when the function
   *   answers another context.
   */
  public constructor(private readonly source: CompileContext | (() => CompileContext)) {}

  /**
   * The context that stands now.
   *
   * @returns The context.
   */
  private get context(): CompileContext {
    return typeof this.source === "function" ? this.source() : this.source;
  }

  /**
   * The context's face declarations checked once for each context value.
   *
   * @returns The declarations `validateTerrainFaces` rejects and the ids of their terrains.
   */
  private get faces(): { readonly malformed: readonly TerrainFaceIssue[]; readonly rejected: ReadonlySet<string> } {
    const context = this.context;
    if (this.checked === null || this.checked.context !== context) {
      const malformed = validateTerrainFaces(context.terrains);
      this.checked = { context, malformed, rejected: new Set(malformed.map((issue) => issue.terrain)) };
    }
    return this.checked;
  }

  /**
   * The context's face declarations `validateTerrainFaces` rejects, which the editor treats as absent.
   *
   * @returns The terrains and why, in context order.
   */
  public get malformedFaces(): readonly TerrainFaceIssue[] {
    return this.faces.malformed;
  }

  /**
   * A terrain's face as the editor uses it.
   *
   * @param terrainId Terrain id.
   * @returns The face, or undefined when the terrain is unknown, declares none or declares a malformed one.
   */
  public faceOf(terrainId: string): TerrainFace | undefined {
    return this.faces.rejected.has(terrainId) ? undefined : this.context.terrains.find((t) => t.id === terrainId)?.face;
  }

  /**
   * A terrain as the editor draws it: without its face when the declaration is malformed.
   *
   * @param terrain The context's terrain.
   * @returns The terrain, or a copy without `face`.
   */
  public usableTerrain(terrain: TerrainDefinition): TerrainDefinition {
    if (!this.faces.rejected.has(terrain.id)) {
      return terrain;
    }
    const { face, ...rest } = terrain;
    void face;
    return rest;
  }

  /**
   * Drops every session and every compiled rule stage, for a change of the world's tileset list, which moves
   * the packed tileset index their cells and tiles were built against (ADR-037 amendment).
   */
  public reset(): void {
    this.sessions.clear();
    this.stageCache.clear();
  }

  /**
   * Whether a terrain layer has a session.
   *
   * @param layerId Layer id.
   * @returns True when it has.
   */
  public has(layerId: string): boolean {
    return this.sessions.has(layerId);
  }

  /**
   * The session of a terrain layer.
   *
   * @param layerId Layer id.
   * @returns The session, or undefined for other layers.
   */
  public get(layerId: string): TerrainSession | undefined {
    return this.sessions.get(layerId);
  }

  /**
   * Rebuilds every terrain session whose layer index, terrain or corners no longer match the document, and
   * drops sessions of layers that no longer exist. A layer whose terrain the context lacks has no session: it draws
   * nothing, and the Issues list says why (`missingTerrainIssues`).
   *
   * @param document The world document.
   * @param map The map being edited.
   * @throws Error when a terrain's tileset or its rule set is unknown.
   */
  public rebuild(document: WorldDocument, map: MapDocument): void {
    const seen = new Set<string>();
    map.layers.forEach((layer, layerIndex) => {
      if (layer.kind !== "terrain") {
        return;
      }
      seen.add(layer.id);
      if (!this.context.terrains.some((t) => t.id === layer.terrain)) {
        this.sessions.delete(layer.id);
        return;
      }
      const existing = this.sessions.get(layer.id);
      if (existing !== undefined && existing.input.layerIndex === layerIndex && existing.input.terrain.id === layer.terrain && existing.corners.every((v, i) => v === layer.corners[i])) {
        this.syncFaceHeight(existing, layer);
        return;
      }
      this.sessions.set(layer.id, this.create(document, map, layer, layerIndex));
    });
    for (const id of [...this.sessions.keys()]) {
      if (!seen.has(id)) {
        this.sessions.delete(id);
      }
    }
  }

  /**
   * Copies the document's corners into a session over the given vertices and re-resolves them.
   *
   * @param map The map.
   * @param layerId Terrain layer id.
   * @param indices Changed vertex indices.
   * @returns Cells to repaint, or null when the layer has no session.
   */
  public resync(map: MapDocument, layerId: string, indices: ArrayLike<number>): CellRect | null {
    const session = this.sessions.get(layerId);
    const layer = map.layers.find((l) => l.id === layerId);
    if (session === undefined || layer === undefined || layer.kind !== "terrain") {
      return null;
    }
    const stride = map.width + 1;
    let vx0 = map.width;
    let vy0 = map.height;
    let vx1 = 0;
    let vy1 = 0;
    for (let i = 0; i < indices.length; i += 1) {
      const index = indices[i] as number;
      session.corners[index] = layer.corners[index] as number;
      const vx = index % stride;
      const vy = (index - vx) / stride;
      vx0 = Math.min(vx0, vx);
      vy0 = Math.min(vy0, vy);
      vx1 = Math.max(vx1, vx);
      vy1 = Math.max(vy1, vy);
    }
    return updateTerrainSession(session, { vx0, vy0, vx1, vy1 });
  }

  /**
   * Brings a terrain layer's session to the height the document asks for, re-running its face pass alone.
   *
   * @param map The map.
   * @param layerId Terrain layer id.
   * @returns Cells to repaint, empty (`x1 <= x0`) when the drawn height did not change, or null when the
   *   layer has no session.
   */
  public applyFaceHeight(map: MapDocument, layerId: string): CellRect | null {
    const session = this.sessions.get(layerId);
    const layer = map.layers.find((l) => l.id === layerId);
    if (session === undefined || layer === undefined || layer.kind !== "terrain") {
      return null;
    }
    return this.syncFaceHeight(session, layer) ?? { x0: 0, y0: 0, x1: 0, y1: 0 };
  }

  /**
   * Sets a session's face height to the one the editor draws its layer at, when it differs.
   *
   * @param session The layer's session.
   * @param layer The terrain layer.
   * @returns The cells to repaint, or null when the height already matches or the terrain has no face.
   */
  private syncFaceHeight(session: TerrainSession, layer: TerrainLayer): CellRect | null {
    const height = drawnFaceHeight(layer, this.faceOf(layer.terrain));
    return session.face === null || session.faceHeight === height ? null : setSessionFaceHeight(session, height);
  }

  /**
   * A rendered sample of a terrain at a colour: a square patch on an otherwise empty map, resolved and
   * automapped as the map would be. The margin leaves room for what rules draw outside the painted patch,
   * such as a cliff face three tiles tall below a plateau, with an empty row beyond it. A face is sampled at its
   * own height, since a palette entry is a terrain, not a layer.
   *
   * @param document The world document, for the tileset table.
   * @param seed Map seed.
   * @param terrainId Terrain id.
   * @param colour Wang colour.
   * @param patch Painted patch size in tiles.
   * @returns The sample cells and its size in tiles, or null when the terrain is unknown.
   */
  public sample(document: WorldDocument, seed: number, terrainId: string, colour: number, patch: number): { cells: Uint32Array; size: number } | null {
    const terrain = this.context.terrains.find((t) => t.id === terrainId);
    const tilesetIndex = document.tilesets.findIndex((t) => t.id === terrain?.tileset);
    if (terrain === undefined || tilesetIndex < 0) {
      return null;
    }
    const margin = SAMPLE_MARGIN;
    const size = patch + 2 * margin;
    const stride = size + 1;
    const corners = new Uint8Array(stride * stride);
    for (let y = margin; y <= margin + patch; y += 1) {
      for (let x = margin; x <= margin + patch; x += 1) {
        corners[y * stride + x] = colour;
      }
    }
    const session = createTerrainSession({ terrain: this.usableTerrain(terrain), tilesetIndex, width: size, height: size, corners, seed, layerIndex: 0, stages: this.stagesFor(terrain, document), stageIndexBase: 0 });
    return { cells: session.output, size };
  }

  /**
   * Creates the incremental session for a terrain layer, at the face height the editor draws it at.
   *
   * @param document The world document.
   * @param map The map.
   * @param layer The terrain layer.
   * @param layerIndex Position in `map.layers`.
   * @returns The session.
   * @throws Error when the terrain or its tileset is unknown.
   */
  private create(document: WorldDocument, map: MapDocument, layer: TerrainLayer, layerIndex: number): TerrainSession {
    const terrain = this.context.terrains.find((t) => t.id === layer.terrain);
    if (terrain === undefined) {
      throw new Error(`terrain ${layer.terrain} is not in the compile context`);
    }
    const tilesetIndex = document.tilesets.findIndex((t) => t.id === terrain.tileset);
    if (tilesetIndex < 0) {
      throw new Error(`terrain ${layer.terrain} uses tileset ${terrain.tileset}, which the world does not list`);
    }
    const session = createTerrainSession({ terrain: this.usableTerrain(terrain), tilesetIndex, width: map.width, height: map.height, corners: new Uint8Array(layer.corners), seed: map.seed, layerIndex, stages: this.stagesFor(terrain, document), stageIndexBase: 0 });
    this.syncFaceHeight(session, layer);
    return session;
  }

  /**
   * Compiled stages for a terrain's rule set, compiled once per rule set and tileset order: a stage holds
   * packed tileset indexes, so documents that list their tilesets in another order each get their own.
   *
   * @param terrain The terrain.
   * @param document The world document, for the tileset table.
   * @returns The stages, empty for a terrain without a rule set.
   * @throws Error when the rule set is missing from the context.
   */
  private stagesFor(terrain: TerrainDefinition, document: WorldDocument): CompiledStage[] {
    if (terrain.ruleSet === undefined) {
      return [];
    }
    const key = `${terrain.ruleSet}\u0000${document.tilesets.map((t) => t.id).join("\u0000")}`;
    const cached = this.stageCache.get(key);
    if (cached !== undefined) {
      return cached;
    }
    const ruleSet = this.context.ruleSets.find((r) => r.id === terrain.ruleSet);
    if (ruleSet === undefined) {
      throw new Error(`rule set ${terrain.ruleSet} is not in the context`);
    }
    const stages = compileRuleSetForWorld(ruleSet, document.tilesets);
    this.stageCache.set(key, stages);
    return stages;
  }
}
